import { CacheManager, CacheType, ICache } from '@descomplica/caching-browser';
import { action, computed, makeObservable, observable } from 'mobx';
import { singleton } from 'tsyringe';

import { GetHighestClassificationLevelsCollaboration } from '@/application/collaborations/get-highest-classification-level.collaboration';
import type { ClassifierDto } from '@/application/interfaces/pegasus.gateway';

@singleton()
export class ClassifiersModel {
  @observable highestClassificationLevels: ClassifierDto;
  @observable classifiers: ClassifierDto[];
  @observable selectedClassifier: ClassifierDto;
  @observable shouldShowPaywall = false;

  screenCache: ICache = CacheManager.getCache(CacheType.LOCALSTORAGE, 'library-screen');

  constructor(
    private getHighestClassificationLevelsCollaboration: GetHighestClassificationLevelsCollaboration,
  ) {
    makeObservable(this);
  }

  @computed
  get highestClassificationLevelName() {
    if (this.classifiers.length) {
      return this.classifiers[0].classificationLevel.name.toUpperCase();
    }
  }

  @action
  async getClassifiers() {
    const { data } = await this.getHighestClassificationLevelsCollaboration.run();

    this.classifiers = data;

    if (this.classifiers.length === 0) {
      this.shouldShowPaywall = true;
      this.selectedClassifier = null;
      return;
    }

    const selectedClassifierGuid = await this.getSelectedClassifierGuidFromCache();

    this.selectedClassifier =
      this.classifiers.find((classifier) => classifier.guid === selectedClassifierGuid) ||
      this.classifiers[0];

    await this.screenCache.set(
      'last-selected-option',
      this.selectedClassifier.guid,
      60 * 60 * 1000,
    );
  }

  @action
  async selectClassifier(classifierGuid: string) {
    this.selectedClassifier = this.classifiers.find(
      (classifier) => classifier.guid === classifierGuid,
    );

    await this.screenCache.set('last-selected-option', classifierGuid, 60 * 60 * 1000);
  }

  private async getSelectedClassifierGuidFromCache() {
    const value = await this.screenCache.get('last-selected-option');

    if (typeof value === 'string' && value) {
      return value;
    }
  }
}
